import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, MapPin, Wrench, User, Clock, AlertTriangle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_OPTIONS = [
  { value: "client_request", label: "Solicitud del cliente" },
  { value: "no_availability", label: "Falta de disponibilidad" },
  { value: "internal_change", label: "Cambio interno de taller" },
  { value: "previous_error", label: "Error en cita anterior" },
  { value: "other", label: "Otro" },
];

const FALLBACK_SERVICES = ["Mantenimiento", "Revision pre-ITV", "Cambio aceite", "Diagnostico", "Frenos", "Otros"];

/**
 * Construye el array `services[]` inicial a partir de la cita existente.
 * Reglas de compatibilidad:
 *   1) Si apt.services[] existe y no está vacío → úsalo tal cual.
 *   2) Si no, pero apt.service_names[] existe → derívalo (todos manuales).
 *   3) Si no, pero apt.service_type existe → 1 servicio manual.
 *   4) Si la cita viene de promoción y no hay servicio, usar promo_titulo como
 *      servicio "promotion" (no desmarcable).
 */
function initialServices(apt) {
  if (!apt) return [];
  if (Array.isArray(apt.services) && apt.services.length > 0) {
    return apt.services.map(s => ({
      id: s.id || (s.name || "").toLowerCase().replace(/\s+/g, "_"),
      name: s.name || "",
      category: s.category || "",
      source: s.source || "manual",
    })).filter(s => s.name);
  }
  if (Array.isArray(apt.service_names) && apt.service_names.length > 0) {
    return apt.service_names.filter(Boolean).map(n => ({
      id: n.toLowerCase().replace(/\s+/g, "_"), name: n, category: "", source: "manual",
    }));
  }
  if (apt.service_type) {
    return [{
      id: apt.service_type.toLowerCase().replace(/\s+/g, "_"),
      name: apt.service_type, category: "", source: "manual",
    }];
  }
  if (apt.promo_titulo) {
    return [{
      id: (apt.promo_id || apt.promo_titulo).toString().toLowerCase().replace(/\s+/g, "_"),
      name: apt.promo_titulo, category: "", source: "promotion",
    }];
  }
  return [];
}

function buildCompatType(services) {
  const names = (services || []).map(s => s.name).filter(Boolean);
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} + ${names[1]}`;
  return `${names[0]} + ${names.length - 1} servicios más`;
}

/**
 * RescheduleModal — verifica disponibilidad real antes de aplicar.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - apt: cita actual (con id, date, time_slot, center, center_name, advisor_id, etc.)
 *   - axiosAuth: () => axios con token (para POST /appointments/{id}/reschedule)
 *   - onRescheduled: (updatedApt) => void  (callback para refrescar listado)
 */
export default function RescheduleModal({ open, onClose, apt, axiosAuth, onRescheduled }) {
  const [centers, setCenters] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [closed, setClosed] = useState(false);
  const [serviceOptions, setServiceOptions] = useState(FALLBACK_SERVICES);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  // Iter 191 — Plazo mínimo del servicio (informativo en admin; NO bloquea)
  const [minDateInfo, setMinDateInfo] = useState(null);

  const [f, setF] = useState({
    center: "",
    center_name: "",
    services: [],
    advisor_id: "",
    advisor_name: "",
    date: "",
    time_slot: "",
    reschedule_reason: "client_request",
    reschedule_notes: "",
  });

  // Reset state on open + cargar servicios actuales con compatibilidad
  useEffect(() => {
    if (!open || !apt) return;
    setF({
      center: apt.center || "",
      center_name: apt.center_name || "",
      services: initialServices(apt),
      advisor_id: apt.advisor_id || "",
      advisor_name: apt.advisor_name || "",
      date: "",
      time_slot: "",
      reschedule_reason: "client_request",
      reschedule_notes: "",
    });
    setSlots([]);
    setClosed(false);
  }, [open, apt]);

  // Load centers + service catalog
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/centers`).then(r => setCenters(r.data || [])).catch(() => setCenters([]));
    axios.get(`${API}/config`).then(r => {
      const cfg = r.data || {};
      const svcList = (cfg.services || [])
        .filter(s => typeof s === "string" || s.active !== false)
        .map(s => typeof s === "string" ? s : s.name)
        .filter(Boolean);
      if (svcList.length) setServiceOptions(svcList);
    }).catch(() => { /* fallback ya inicializado */ });
  }, [open]);

  useEffect(() => {
    if (!f.center) { setAdvisors([]); return; }
    axios.get(`${API}/advisors/by-center/${f.center}`)
      .then(r => setAdvisors(r.data || []))
      .catch(() => setAdvisors([]));
  }, [f.center]);

  // Re-fetch availability when date/center/advisor changes
  useEffect(() => {
    if (!open || !f.date || !f.center) { setSlots([]); setClosed(false); return; }
    setLoadingSlots(true);
    const advParam = f.advisor_id ? `&advisor_id=${f.advisor_id}` : "";
    axios.get(`${API}/appointments/availability?date=${f.date}&center=${f.center}${advParam}`)
      .then(r => {
        setSlots(r.data.slots || []);
        setClosed(!!r.data.closed);
      })
      .catch(() => { setSlots([]); setClosed(false); })
      .finally(() => setLoadingSlots(false));
  }, [open, f.date, f.center, f.advisor_id]);

  // Iter 191 — Recalcular plazo mínimo cuando cambian servicios o centro
  useEffect(() => {
    if (!open) return;
    const slugs = [];
    for (const s of (f.services || [])) {
      const slug = s.slug || (s.id || s.name || "").toString().toLowerCase().replace(/\s+/g, "-");
      if (slug) slugs.push(slug);
    }
    if (slugs.length === 0) { setMinDateInfo(null); return; }
    axios.post(`${API}/appointment-services/min-date`, {
      service_slugs: slugs, center: f.center || "",
    }).then(r => setMinDateInfo(r.data)).catch(() => setMinDateInfo(null));
  }, [open, f.services, f.center]);

  const dateBelowMin = useMemo(() => {
    if (!f.date || !minDateInfo?.min_date) return false;
    return f.date < minDateInfo.min_date;
  }, [f.date, minDateInfo]);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  // Agrupación por franja
  const groupedSlots = useMemo(() => {
    const morning = [], midday = [], afternoon = [];
    for (const s of slots) {
      if (!s.available) continue;
      const h = parseInt((s.slot || "").split(":")[0], 10);
      if (h < 12) morning.push(s);
      else if (h < 16) midday.push(s);
      else afternoon.push(s);
    }
    return { morning, midday, afternoon };
  }, [slots]);

  const handleSubmit = async () => {
    if (!f.date) return toast.error("Selecciona una fecha");
    if (!f.time_slot) return toast.error("Selecciona una franja horaria");
    if (!f.reschedule_reason) return toast.error("Indica el motivo de la reprogramación");
    if (!Array.isArray(f.services) || f.services.length === 0) return toast.error("Selecciona al menos un servicio");
    setSubmitting(true);
    try {
      const service_names = f.services.map(s => s.name).filter(Boolean);
      const primary_service = service_names[0] || "";
      const compatType = buildCompatType(f.services);
      const payload = {
        date: f.date,
        time_slot: f.time_slot,
        center: f.center,
        center_name: f.center_name,
        service_type: compatType,
        services: f.services,
        service_names,
        primary_service,
        advisor_id: f.advisor_id,
        advisor_name: f.advisor_name,
        reschedule_reason: f.reschedule_reason,
        reschedule_notes: f.reschedule_notes,
      };
      const res = await axiosAuth().post(`/appointments/${apt.id}/reschedule`, payload);
      toast.success("Reprogramación confirmada y email enviado al cliente.");
      onRescheduled && onRescheduled(res.data);
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al reprogramar";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!apt) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="reschedule-modal">
        <DialogHeader>
          <DialogTitle className="heading-industrial text-lg uppercase flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-amber-600" /> Reprogramar cita
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 pt-1">
            Se verificará la disponibilidad real antes de aplicar el cambio. La cita mantiene
            su ID y se conservan los datos del cliente, vehículo y vehículo de alquiler.
          </DialogDescription>
        </DialogHeader>

        {/* Resumen de la cita actual */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs"
             data-testid="reschedule-current-summary">
          <span className="text-slate-500">Cliente</span>
          <span className="font-medium text-slate-900">{apt.client_name}</span>
          <span className="text-slate-500">Matrícula</span>
          <span className="font-mono font-semibold text-slate-900">{apt.plate}</span>
          <span className="text-slate-500 self-start pt-0.5">Servicios actuales</span>
          <span className="text-slate-900" data-testid="reschedule-current-services">
            {(() => {
              const cur = initialServices(apt);
              if (cur.length === 0) return <span className="text-slate-400">—</span>;
              return (
                <ul className="list-disc list-inside space-y-0.5">
                  {cur.map((s, i) => (
                    <li key={i} className={s.source === "promotion" ? "text-amber-800" : ""}>
                      {s.name}
                      {s.source === "promotion" && <span className="ml-1 text-[9px] uppercase tracking-wider text-amber-700 font-semibold">(Promoción)</span>}
                    </li>
                  ))}
                </ul>
              );
            })()}
          </span>
          <span className="text-slate-500">Centro actual</span>
          <span className="text-slate-900">{apt.center_name || apt.center || "—"}</span>
          <span className="text-slate-500">Fecha actual</span>
          <span className="font-mono text-slate-900">{apt.date} · {apt.time_slot}</span>
          {apt.advisor_name && (<>
            <span className="text-slate-500">Asesor actual</span>
            <span className="text-slate-900">{apt.advisor_name}</span>
          </>)}
          <span className="text-slate-500">Estado actual</span>
          <span className="text-slate-900">{apt.status}</span>
          {(apt.replacement_vehicle_needed && apt.replacement_vehicle_needed !== "no") && (<>
            <span className="text-amber-700">Veh. alquiler</span>
            <span className="text-amber-900 font-medium">
              {apt.replacement_vehicle_needed === "yes" ? "Sí" : "Consultar"} — se conservará
            </span>
          </>)}
        </div>

        {/* Selectores de nueva fecha/centro/asesor */}
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Centro
              </Label>
              <Select value={f.center} onValueChange={(v) => {
                const c = centers.find(x => x.slug === v);
                set("center", v);
                set("center_name", c?.name || "");
                set("time_slot", "");
              }}>
                <SelectTrigger className="h-9 rounded-sm text-sm border-slate-200" data-testid="reschedule-center">
                  <SelectValue placeholder="Selecciona centro" />
                </SelectTrigger>
                <SelectContent>
                  {centers.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Selector múltiple de servicios (idéntico al formulario público /cita) */}
            <div className="space-y-1.5" data-testid="reschedule-services-block">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <Wrench className="w-3 h-3" /> Servicios * (puedes elegir varios)
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {serviceOptions.map(s => {
                  const selected = (f.services || []).some(x => x.name === s);
                  const isPromo = (f.services || []).find(x => x.name === s && x.source === "promotion");
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        const current = f.services || [];
                        const exists = current.find(x => x.name === s);
                        let next;
                        if (exists) {
                          // No permitir deseleccionar la promoción (igual que en /cita)
                          if (exists.source === "promotion") return;
                          next = current.filter(x => x.name !== s);
                        } else {
                          next = [...current, { id: s.toLowerCase().replace(/\s+/g, "_"), name: s, category: "", source: "manual" }];
                        }
                        set("services", next);
                      }}
                      data-testid={`reschedule-service-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={
                        "rounded-sm border px-3 py-2 text-xs uppercase tracking-wider font-semibold transition-all text-left " +
                        (selected
                          ? (isPromo
                            ? "border-amber-500 bg-amber-50 text-amber-900"
                            : "border-red-500 bg-red-50 text-red-900")
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")
                      }
                    >
                      {selected ? "✓ " : ""}{s}
                      {isPromo && <span className="block text-[9px] mt-0.5 text-amber-700 font-normal normal-case">(Promoción)</span>}
                    </button>
                  );
                })}
                {/* Si hay servicios en la cita que NO están en el catálogo (p.ej. promo custom o catálogo cambió) los mostramos como chips ya seleccionados */}
                {(f.services || []).filter(sv => !serviceOptions.includes(sv.name)).map(sv => {
                  const isPromo = sv.source === "promotion";
                  return (
                    <button
                      key={`extra-${sv.name}`}
                      type="button"
                      onClick={() => {
                        if (isPromo) return;
                        set("services", (f.services || []).filter(x => x.name !== sv.name));
                      }}
                      data-testid={`reschedule-service-extra-${(sv.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                      className={
                        "rounded-sm border px-3 py-2 text-xs uppercase tracking-wider font-semibold transition-all text-left " +
                        (isPromo
                          ? "border-amber-500 bg-amber-50 text-amber-900"
                          : "border-red-500 bg-red-50 text-red-900")
                      }
                    >
                      ✓ {sv.name}
                      {isPromo && <span className="block text-[9px] mt-0.5 text-amber-700 font-normal normal-case">(Promoción)</span>}
                    </button>
                  );
                })}
              </div>
              {(f.services || []).length > 1 && (
                <p className="text-[11px] text-slate-500">
                  Servicio principal: <strong>{f.services[0].name}</strong> · Total: {f.services.length} servicios
                </p>
              )}
              {(f.services || []).length === 0 && (
                <p className="text-[11px] text-red-600">Selecciona al menos un servicio.</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Nueva fecha
              </Label>
              <input
                type="date"
                value={f.date}
                min={today}
                onChange={(e) => { set("date", e.target.value); set("time_slot", ""); }}
                className="w-full h-9 rounded-sm border border-slate-200 px-2 text-sm font-mono"
                data-testid="reschedule-date"
              />
              {dateBelowMin && minDateInfo?.min_date && (
                <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="reschedule-below-min-warning">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>
                    Fecha anterior al plazo recomendado del servicio
                    {minDateInfo.governing_service_name ? ` (${minDateInfo.governing_service_name})` : ""}.
                    Primera fecha recomendada: <strong>{minDateInfo.min_date}</strong>
                    {minDateInfo.applied_delay_label ? ` (${minDateInfo.applied_delay_label})` : ""}.
                    Permitido por gestión interna; quedará registrado como override.
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <User className="w-3 h-3" /> Asesor
              </Label>
              <Select value={f.advisor_id || "none"} onValueChange={(v) => {
                if (v === "none") { set("advisor_id", ""); set("advisor_name", ""); set("time_slot", ""); return; }
                const a = advisors.find(x => x.id === v);
                set("advisor_id", v);
                set("advisor_name", a?.name || "");
                set("time_slot", "");
              }}>
                <SelectTrigger className="h-9 rounded-sm text-sm border-slate-200" data-testid="reschedule-advisor">
                  <SelectValue placeholder="Sin preferencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin preferencia</SelectItem>
                  {advisors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Slots */}
          <div className="space-y-2 pt-1" data-testid="reschedule-slots-block">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
              <Clock className="w-3 h-3" /> Huecos disponibles
            </Label>
            {!f.date || !f.center ? (
              <p className="text-xs text-slate-400">Selecciona centro y fecha para ver huecos.</p>
            ) : loadingSlots ? (
              <p className="text-xs text-slate-400">Buscando disponibilidad...</p>
            ) : closed ? (
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5" /> El centro no abre este día.
              </div>
            ) : (groupedSlots.morning.length === 0 && groupedSlots.midday.length === 0 && groupedSlots.afternoon.length === 0) ? (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800"
                   data-testid="reschedule-no-slots">
                <AlertTriangle className="w-3.5 h-3.5" /> No hay huecos disponibles para esta fecha. Prueba con otro día o centro.
              </div>
            ) : (
              <div className="space-y-2">
                {[
                  { key: "morning", label: "Mañana", arr: groupedSlots.morning },
                  { key: "midday", label: "Mediodía", arr: groupedSlots.midday },
                  { key: "afternoon", label: "Tarde", arr: groupedSlots.afternoon },
                ].filter(g => g.arr.length > 0).map(g => (
                  <div key={g.key} data-testid={`reschedule-group-${g.key}`}>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{g.label}</p>
                    <div className="grid grid-cols-6 gap-1.5">
                      {g.arr.map(s => (
                        <button
                          key={s.slot}
                          type="button"
                          onClick={() => set("time_slot", s.slot)}
                          data-testid={`reschedule-slot-${s.slot}`}
                          className={
                            "h-8 rounded-sm border text-xs font-mono transition-colors " +
                            (f.time_slot === s.slot
                              ? "bg-amber-500 border-amber-500 text-white font-bold"
                              : "border-slate-200 text-slate-700 hover:border-amber-400 hover:bg-amber-50")
                          }
                        >
                          {s.slot}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* COMPARACIÓN: Cambio propuesto (M1) */}
          {f.date && f.time_slot ? (
            <div className="border-2 border-amber-300 bg-amber-50/60 rounded-sm p-3 space-y-2" data-testid="reschedule-comparison">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Cambio propuesto
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-[60px_1fr] gap-x-2 gap-y-1 text-xs font-mono">
                <span className="text-slate-500 font-sans uppercase text-[10px] tracking-wider self-center">De:</span>
                <span data-testid="reschedule-comparison-from" className="text-slate-700 line-through decoration-red-400">
                  {apt.date} · {apt.time_slot} · {apt.center_name || apt.center}{apt.advisor_name ? ` · ${apt.advisor_name}` : ""}
                </span>
                <span className="text-slate-500 font-sans uppercase text-[10px] tracking-wider self-center">A:</span>
                <span data-testid="reschedule-comparison-to" className="text-amber-900 font-bold">
                  {f.date} · {f.time_slot} · {f.center_name || f.center}{f.advisor_name ? ` · ${f.advisor_name}` : ""}
                </span>
              </div>
              {/* Nuevos servicios completos */}
              <div className="border-t border-amber-200 pt-2 mt-2 text-[11px] text-amber-900" data-testid="reschedule-new-services">
                <p className="font-semibold uppercase tracking-wider text-[10px] mb-1">Nuevos servicios:</p>
                {(f.services || []).length === 0 ? (
                  <span className="text-red-700">— Sin servicios seleccionados —</span>
                ) : (
                  <ul className="list-disc list-inside space-y-0.5 font-sans">
                    {f.services.map((s, i) => (
                      <li key={i} className={s.source === "promotion" ? "text-amber-900 font-semibold" : ""}>
                        {s.name}
                        {s.source === "promotion" && <span className="ml-1 text-[9px] uppercase tracking-wider text-amber-700 font-semibold">(Promoción)</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Aviso si tenía vehículo de alquiler */}
              {(apt.replacement_vehicle_needed && apt.replacement_vehicle_needed !== "no") && (
                <div className="border-t border-amber-200 pt-2 mt-2 flex items-start gap-2 text-[11px] text-amber-900"
                     data-testid="reschedule-rv-warning">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-700" />
                  <span>
                    Esta cita tiene <strong>vehículo de alquiler
                    {apt.replacement_vehicle_needed === "yes" ? " solicitado" : " (consultar)"}</strong>.
                    Revisa disponibilidad antes de confirmar la nueva fecha.
                  </span>
                </div>
              )}
              {/* Promoción asociada */}
              {(apt.promo_titulo || apt.promo_id) && (
                <div className="border-t border-amber-200 pt-2 mt-2 text-[11px] text-amber-900" data-testid="reschedule-promo-info">
                  <span className="font-semibold">Promoción:</span> {apt.promo_titulo || apt.promo_id}
                  {apt.promo_precio ? ` · ${apt.promo_precio}` : ""}
                </div>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-slate-200 rounded-sm p-3 text-xs text-slate-400 text-center"
                 data-testid="reschedule-comparison-placeholder">
              Selecciona fecha y hora para ver la nueva cita.
            </div>
          )}

          {/* Motivo y notas */}
          <div className="grid grid-cols-1 gap-3 pt-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Motivo de la reprogramación *
              </Label>
              <Select value={f.reschedule_reason} onValueChange={(v) => set("reschedule_reason", v)}>
                <SelectTrigger className="h-9 rounded-sm text-sm border-slate-200" data-testid="reschedule-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Notas internas (opcional)
              </Label>
              <Textarea
                value={f.reschedule_notes}
                onChange={(e) => set("reschedule_notes", e.target.value)}
                placeholder="Detalles adicionales sobre el motivo del cambio..."
                className="rounded-sm text-sm h-16 resize-none"
                data-testid="reschedule-notes"
                maxLength={500}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}
            className="rounded-sm text-xs uppercase" data-testid="reschedule-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !f.date || !f.time_slot || (f.services || []).length === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs uppercase font-semibold"
            data-testid="reschedule-confirm-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            {submitting ? "Reprogramando..." : "Confirmar reprogramación"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
